/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import {
  ccmClusterRoleDescriptions,
  ccmClusterRoleLabels,
  deploymentRoleDescriptions,
  deploymentRoleLabels,
  elasticsearchRoleDescriptions,
  elasticsearchRoleLabels,
  observabilityRoleDescriptions,
  observabilityRoleLabels,
  securityRoleDescriptions,
  securityRoleLabels,
} from './messages'
import {
  ccmClusterRoleIdsByLabel,
  deploymentRoleIdsByLabel,
  elasticsearchRoleIdsByLabel,
  observabilityRoleIdsByLabel,
  securityRoleIdsByLabel,
  type RoleOptionDefinition,
} from './types'

export const deploymentOptions: RoleOptionDefinition[] = [
  {
    roleId: deploymentRoleIdsByLabel.admin,
    labelDescriptor: deploymentRoleLabels.admin,
    descriptionDescriptor: deploymentRoleDescriptions.admin,
  },
  {
    roleId: deploymentRoleIdsByLabel.editor,
    labelDescriptor: deploymentRoleLabels.editor,
    descriptionDescriptor: deploymentRoleDescriptions.editor,
  },
  {
    roleId: deploymentRoleIdsByLabel.viewer,
    labelDescriptor: deploymentRoleLabels.viewer,
    descriptionDescriptor: deploymentRoleDescriptions.viewer,
  },
]

export const elasticsearchOptions: RoleOptionDefinition[] = [
  {
    roleId: elasticsearchRoleIdsByLabel.admin,
    labelDescriptor: elasticsearchRoleLabels.admin,
    descriptionDescriptor: elasticsearchRoleDescriptions.admin,
  },
  {
    roleId: elasticsearchRoleIdsByLabel.developer,
    labelDescriptor: elasticsearchRoleLabels.developer,
    descriptionDescriptor: elasticsearchRoleDescriptions.developer,
  },
  {
    roleId: elasticsearchRoleIdsByLabel.viewer,
    labelDescriptor: elasticsearchRoleLabels.viewer,
    descriptionDescriptor: elasticsearchRoleDescriptions.viewer,
  },
]

export const observabilityOptions: RoleOptionDefinition[] = [
  {
    roleId: observabilityRoleIdsByLabel.admin,
    labelDescriptor: observabilityRoleLabels.admin,
    descriptionDescriptor: observabilityRoleDescriptions.admin,
  },
  {
    roleId: observabilityRoleIdsByLabel.editor,
    labelDescriptor: observabilityRoleLabels.editor,
    descriptionDescriptor: observabilityRoleDescriptions.editor,
  },
  {
    roleId: observabilityRoleIdsByLabel.viewer,
    labelDescriptor: observabilityRoleLabels.viewer,
    descriptionDescriptor: observabilityRoleDescriptions.viewer,
  },
]

export const securityOptions: RoleOptionDefinition[] = [
  {
    roleId: securityRoleIdsByLabel.admin,
    labelDescriptor: securityRoleLabels.admin,
    descriptionDescriptor: securityRoleDescriptions.admin,
  },
  {
    roleId: securityRoleIdsByLabel.editor,
    labelDescriptor: securityRoleLabels.editor,
    descriptionDescriptor: securityRoleDescriptions.editor,
  },
  {
    roleId: securityRoleIdsByLabel.viewer,
    labelDescriptor: securityRoleLabels.viewer,
    descriptionDescriptor: securityRoleDescriptions.viewer,
  },
  {
    roleId: securityRoleIdsByLabel.tier1Analyst,
    labelDescriptor: securityRoleLabels.tier1Analyst,
    descriptionDescriptor: securityRoleDescriptions.tier1Analyst,
  },
  {
    roleId: securityRoleIdsByLabel.tier2Analyst,
    labelDescriptor: securityRoleLabels.tier2Analyst,
    descriptionDescriptor: securityRoleDescriptions.tier2Analyst,
  },
  {
    roleId: securityRoleIdsByLabel.tier3Analyst,
    labelDescriptor: securityRoleLabels.tier3Analyst,
    descriptionDescriptor: securityRoleDescriptions.tier3Analyst,
  },
  {
    roleId: securityRoleIdsByLabel.threatIntelligenceAnalyst,
    labelDescriptor: securityRoleLabels.threatIntelligenceAnalyst,
    descriptionDescriptor: securityRoleDescriptions.threatIntelligenceAnalyst,
  },
  {
    roleId: securityRoleIdsByLabel.ruleAuthor,
    labelDescriptor: securityRoleLabels.ruleAuthor,
    descriptionDescriptor: securityRoleDescriptions.ruleAuthor,
  },
  {
    roleId: securityRoleIdsByLabel.socManager,
    labelDescriptor: securityRoleLabels.socManager,
    descriptionDescriptor: securityRoleDescriptions.socManager,
  },
  {
    roleId: securityRoleIdsByLabel.endpointOperationsAnalyst,
    labelDescriptor: securityRoleLabels.endpointOperationsAnalyst,
    descriptionDescriptor: securityRoleDescriptions.endpointOperationsAnalyst,
  },
  {
    roleId: securityRoleIdsByLabel.platformEngineer,
    labelDescriptor: securityRoleLabels.platformEngineer,
    descriptionDescriptor: securityRoleDescriptions.platformEngineer,
  },
  {
    roleId: securityRoleIdsByLabel.detectionsAdmin,
    labelDescriptor: securityRoleLabels.detectionsAdmin,
    descriptionDescriptor: securityRoleDescriptions.detectionsAdmin,
  },
  {
    roleId: securityRoleIdsByLabel.endpointPolicyManager,
    labelDescriptor: securityRoleLabels.endpointPolicyManager,
    descriptionDescriptor: securityRoleDescriptions.endpointPolicyManager,
  },
]

export const securityOptionsWithAiSocEngine: RoleOptionDefinition[] = [
  {
    roleId: securityRoleIdsByLabel.aiSocEngineAnalyst,
    labelDescriptor: securityRoleLabels.aiSocEngineAnalyst,
    descriptionDescriptor: securityRoleDescriptions.aiSocEngineAnalyst,
  },
  {
    roleId: securityRoleIdsByLabel.aiSocEngineSocManager,
    labelDescriptor: securityRoleLabels.aiSocEngineSocManager,
    descriptionDescriptor: securityRoleDescriptions.aiSocEngineSocManager,
  },
]

// TODO: remove this and replace all imported instances of
// securityOptionsWithSearchAiLake with securityOptionsWithAiSocEngine
export const securityOptionsWithSearchAiLake: RoleOptionDefinition[] = [
  {
    roleId: securityRoleIdsByLabel.searchAiLakeAnalyst,
    labelDescriptor: securityRoleLabels.searchAiLakeAnalyst,
    descriptionDescriptor: securityRoleDescriptions.searchAiLakeAnalyst,
  },
  {
    roleId: securityRoleIdsByLabel.searchAiLakeSocManager,
    labelDescriptor: securityRoleLabels.searchAiLakeSocManager,
    descriptionDescriptor: securityRoleDescriptions.searchAiLakeSocManager,
  },
]

export const ccmClusterOptions: RoleOptionDefinition[] = [
  {
    roleId: ccmClusterRoleIdsByLabel.admin,
    labelDescriptor: ccmClusterRoleLabels.admin,
    descriptionDescriptor: ccmClusterRoleDescriptions.admin,
  },
  {
    roleId: ccmClusterRoleIdsByLabel.viewer,
    labelDescriptor: ccmClusterRoleLabels.viewer,
    descriptionDescriptor: ccmClusterRoleDescriptions.viewer,
  },
]
